import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import BadgeComponent from '../../../../components/Badge.component';
import { useAppSelector } from '../../../../hooks/useAppSelector.hook';
import {
  getAssistantCandidateCachedEnglish,
  getAssistantCandidateCachedName,
} from '../../../../redux/Assistant/Assistant.slice';
import { RoutesConstant } from '../../../../constants/routes.constant';
import { LocationState } from '../../../../types/locationState.interface';
import { AssistantCandidate } from '../../../../types/assistant.types';
import {
  formatAssistantExperienceYears,
  getAssistantCandidateEnglish,
  getAssistantCandidateId,
  getAssistantCandidateName,
  getAssistantCandidatePath,
  getMatchScorePercent,
} from '../../../../utils/assistant.utils';
import {
  StyledCandidateAchievements,
  StyledCandidateCard,
  StyledCandidateCardActions,
  StyledCandidateCardHeader,
  StyledCandidateCardHeaderInfo,
  StyledCandidateSection,
  StyledCandidateSectionTitle,
  StyledCandidateSkills,
  StyledCandidateSummary,
  StyledCandidateTitle,
  StyledCompactCandidateCard,
  StyledCompactCandidateHeader,
  StyledCompactCandidateInfo,
  StyledCompactMatchScore,
  StyledCompactMetaFixed,
  StyledCompactMetaRole,
  StyledCompactMetaSecondary,
  StyledCompactMetaSep,
  StyledMatchScoreBadge,
  StyledOpenAssistantLink,
  StyledOpenCandidateLink,
  StyledToggleDetails,
} from '../Assistant.styles';

export type CandidateResultCardVariant = 'full' | 'compact';

const SKILLS_PREVIEW_LIMIT = 8;

interface CandidateResultCardProps {
  candidate: AssistantCandidate;
  canOpenCandidate: boolean;
  variant?: CandidateResultCardVariant;
  onOpenAssistant?: () => void;
}

const CandidateResultCard = ({
  candidate,
  canOpenCandidate,
  variant = 'full',
  onOpenAssistant,
}: CandidateResultCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setExpanded] = useState(false);
  const candidateId = getAssistantCandidateId(candidate);
  const cachedName = useAppSelector(state =>
    getAssistantCandidateCachedName(state, candidateId),
  );
  const cachedEnglish = useAppSelector(state =>
    getAssistantCandidateCachedEnglish(state, candidateId),
  );
  const resolvedName = getAssistantCandidateName(candidate, {
    cachedName,
  });
  const isNamePlaceholder = !resolvedName;
  const displayName = resolvedName || t('Candidate');
  const candidatePath = candidateId
    ? getAssistantCandidatePath(candidateId)
    : '';
  const canOpen = canOpenCandidate && !!candidateId;
  const matchPercent = getMatchScorePercent(candidate);
  const skills = candidate.cv_key_skills ?? [];
  const visibleSkills = isExpanded
    ? skills
    : skills.slice(0, SKILLS_PREVIEW_LIMIT);
  const hiddenSkillsCount = skills.length - visibleSkills.length;
  const hasDetails =
    Boolean(candidate.evaluation_summary) ||
    Boolean(candidate.cv_achievements?.length) ||
    skills.length > SKILLS_PREVIEW_LIMIT ||
    Boolean(candidate.cv_summary);
  const locationState = {
    backUrl: location.pathname,
    backUrlQuery: location.search,
  } as LocationState;
  const roleText = candidate.cv_main_role?.trim() || '';
  const experienceText =
    typeof candidate.cv_experience_years === 'number'
      ? formatAssistantExperienceYears(candidate.cv_experience_years, {
          one: t('assistant.year'),
          few: t('assistant.yearsFew'),
          many: t('assistant.years'),
        })
      : '';
  const englishText = getAssistantCandidateEnglish(candidate, {
    cachedEnglish,
  });
  const secondaryMetaParts = [experienceText, englishText].filter(Boolean);

  const renderMetaBlock = () => (
    <>
      {roleText ? (
        <StyledCompactMetaRole title={roleText}>{roleText}</StyledCompactMetaRole>
      ) : null}
      {secondaryMetaParts.length > 0 && (
        <StyledCompactMetaSecondary>
          {secondaryMetaParts.map((part, index) => (
            <React.Fragment key={`${part}-${index}`}>
              {index > 0 && <StyledCompactMetaSep>·</StyledCompactMetaSep>}
              <StyledCompactMetaFixed>{part}</StyledCompactMetaFixed>
            </React.Fragment>
          ))}
        </StyledCompactMetaSecondary>
      )}
    </>
  );

  const handleOpenCandidate = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (!canOpen) {
      event.preventDefault();
      return;
    }

    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    navigate(candidatePath, { state: locationState });
  };

  const handleOpenAssistant = (
    event?: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (onOpenAssistant) {
      onOpenAssistant();
      return;
    }

    navigate(RoutesConstant.ASSISTANT);
  };

  const compactBody = (
    <>
      <StyledCompactCandidateHeader>
        <StyledCompactCandidateInfo>
          <StyledCandidateTitle $muted={isNamePlaceholder}>
            {displayName}
          </StyledCandidateTitle>
          {renderMetaBlock()}
        </StyledCompactCandidateInfo>
        <StyledCompactMatchScore aria-label={t('assistant.matchScore')}>
          {matchPercent}%
        </StyledCompactMatchScore>
      </StyledCompactCandidateHeader>
    </>
  );

  if (variant === 'compact') {
    if (!canOpen) {
      return (
        <StyledCompactCandidateCard as="div">
          {compactBody}
        </StyledCompactCandidateCard>
      );
    }

    return (
      <StyledCompactCandidateCard
        href={candidatePath}
        onClick={handleOpenCandidate}
      >
        {compactBody}
      </StyledCompactCandidateCard>
    );
  }

  return (
    <StyledCandidateCard
      $clickable={canOpen}
      onClick={canOpen ? handleOpenCandidate : undefined}
    >
      <StyledCandidateCardHeader>
        <StyledCandidateCardHeaderInfo>
          <StyledCandidateTitle $muted={isNamePlaceholder}>
            {displayName}
          </StyledCandidateTitle>
          {renderMetaBlock()}
        </StyledCandidateCardHeaderInfo>
        <StyledMatchScoreBadge>
          <span>{t('assistant.matchScore')}</span>
          <strong>{matchPercent}%</strong>
        </StyledMatchScoreBadge>
      </StyledCandidateCardHeader>

      {visibleSkills.length > 0 && (
        <StyledCandidateSection>
          <StyledCandidateSectionTitle>
            {t('assistant.skills')}
          </StyledCandidateSectionTitle>
          <StyledCandidateSkills>
            {visibleSkills.map((skill: string, index: number) => (
              <BadgeComponent
                key={`${skill}-${index}`}
                appearance="default"
                text={skill}
                rounded
              />
            ))}
            {!isExpanded && hiddenSkillsCount > 0 && (
              <BadgeComponent
                appearance="default"
                text={`+${hiddenSkillsCount}`}
                rounded
              />
            )}
          </StyledCandidateSkills>
        </StyledCandidateSection>
      )}

      {candidate.cv_summary && (
        <StyledCandidateSummary $clamp={!isExpanded}>
          {candidate.cv_summary}
        </StyledCandidateSummary>
      )}

      {isExpanded && candidate.evaluation_summary && (
        <StyledCandidateSection>
          <StyledCandidateSectionTitle>
            {t('assistant.evaluation')}
          </StyledCandidateSectionTitle>
          {candidate.evaluation_summary}
        </StyledCandidateSection>
      )}

      {isExpanded && candidate.cv_achievements?.length > 0 && (
        <StyledCandidateSection>
          <StyledCandidateSectionTitle>
            {t('assistant.achievements')}
          </StyledCandidateSectionTitle>
          <StyledCandidateAchievements>
            {candidate.cv_achievements.map((achievement: string, index: number) => (
              <li key={`${achievement}-${index}`}>{achievement}</li>
            ))}
          </StyledCandidateAchievements>
        </StyledCandidateSection>
      )}

      {hasDetails && (
        <StyledToggleDetails
          type="button"
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            setExpanded(previous => !previous);
          }}
        >
          {isExpanded ? t('assistant.hideDetails') : t('assistant.showDetails')}
        </StyledToggleDetails>
      )}

      <StyledCandidateCardActions>
        {canOpen && (
          <StyledOpenCandidateLink
            href={candidatePath}
            onClick={handleOpenCandidate}
          >
            {t('assistant.openCandidate')}
          </StyledOpenCandidateLink>
        )}
        {onOpenAssistant && (
          <StyledOpenAssistantLink type="button" onClick={handleOpenAssistant}>
            {t('assistant.openInAssistant')}
          </StyledOpenAssistantLink>
        )}
      </StyledCandidateCardActions>
    </StyledCandidateCard>
  );
};

export default CandidateResultCard;
